// Barra lateral direita, na ordem do cliente: minimapa, vida e mana, equipamento,
// combate, controles, Battle List e Skills.
import { useState } from 'preact/hooks';
import type { CharacterState } from '../../../../shared/src/protocol';
import { BattleList, type BattleEntry } from './BattleList';
import { Skills } from './Skills';
import type { ControlButton } from './controls';
import { MenuPanel } from './MenuUI';
import { ItemSlot, type DragApi } from './ItemSlot';
import { ContainerWidget, PouchWidget } from './Containers';
import { POUCH_WINDOW } from '../../../../shared/src/game/supply';
import { Minimap, type MinimapPos } from './Minimap';
import type { EquipSlot, ItemInstance } from '../../../../shared/src/game/items';

export interface InvProps { items: ItemInstance[]; freeCapacity: number | null; api: DragApi; open: number[]; closeContainer: (id: number) => void }

const IMG = '/assets/interface/images';

function ImgButton(props: { up: string; down?: string; title: string; onClick?: () => void; w?: number; h?: number; active?: boolean }) {
  const [pressed, setPressed] = useState(false);
  const src = (pressed || props.active) && props.down ? props.down : props.up;
  return (
    <button class="img-btn" title={props.title} aria-label={props.title} onClick={props.onClick}
      onPointerDown={() => setPressed(true)} onPointerUp={() => setPressed(false)} onPointerLeave={() => setPressed(false)}>
      <img src={src} width={props.w ?? 20} height={props.h ?? 20} alt="" />
    </button>
  );
}

function StatusBars(props: { c: CharacterState; monk?: { harmony: number; serene: boolean } }) {
  const { c } = props;
  const bar = (v: number, max: number, fill: string) => (
    <span class="sb-bar"><i style={{ width: (max ? Math.min(100, (v / max) * 100) : 0) + '%', backgroundImage: `url(${IMG}/${fill})` }} /></span>
  );
  return (
    <div class="pane status">
      <div><img src={`${IMG}/hitpoints-symbol.png`} alt="Vida" />{bar(c.health, c.maxHealth, 'hitpoints-bar-filled.png')}<span>{c.health}</span></div>
      <div><img src={`${IMG}/mana-symbol.png`} alt="Mana" />{bar(c.mana, c.maxMana, 'mana-bar-filled.png')}<span>{c.mana}</span></div>
      {props.monk && (
        <div class="monk-row" title="Harmony: os builders geram 1 carga (até 5); os spenders gastam todas e causam mais dano. Serene: sem party por perto, as virtudes ficam mais fortes.">
          <img src={`${IMG}/icon-serene-${props.monk.serene ? 'on' : 'off'}.png`} alt="Serene" title={props.monk.serene ? 'Sereno' : 'Não sereno'} />
          {[0, 1, 2, 3, 4].map((i) => <img key={i} src={`${IMG}/icon-combopoint-${i < props.monk!.harmony ? 'filled' : 'empty'}.png`} alt="" />)}
          <span class="small">Harmony {props.monk.harmony}/5</span>
        </div>
      )}
    </div>
  );
}

// Slot de equipamento -> imagem de fundo do cliente e nome
const SLOTS: Array<[EquipSlot, string, string]> = [
  ['neck', 'neck', 'Amuleto'], ['head', 'head', 'Capacete'], ['back', 'back', 'Mochila'],
  ['weapon', 'left-hand', 'Arma'], ['armor', 'torso', 'Armadura'], ['shield', 'right-hand', 'Escudo'],
  ['ring', 'finger', 'Anel'], ['legs', 'legs', 'Calça'], ['ammo', 'hip', 'Munição'],
];

/** Ícones de condição (folha player-state-flags.png do cliente, 9x9 cada) e o texto de cada um. */
const FLAG: Record<string, [number, string]> = {
  poison: [0, 'Envenenado'], burning: [1, 'Queimando'], electrified: [2, 'Eletrificado'], drunk: [3, 'Bêbado'], mshield: [4, 'Magic Shield'],
  paralyzed: [5, 'Paralisado'], haste: [6, 'Haste'], battle: [7, 'Em combate'], drowning: [8, 'Afogando'], freezing: [9, 'Congelando'],
  dazzled: [10, 'Ofuscado'], cursed: [11, 'Amaldiçoado'], strengthened: [12, 'Fortalecido'], pz: [14, 'Zona protegida'], bleeding: [15, 'Sangrando'],
};

function Inventory(props: { c: CharacterState; inv: InvProps; flags?: string[]; onOpen: (id: string) => void; onLogout: () => void }) {
  const pal = props.c.vocation === 'paladin';
  const eq = (s: EquipSlot) => props.inv.items.find((i) => i.place === 'equip' && i.slot === s);
  const slot = (s: EquipSlot, img: string, label: string) => s === 'ammo' && pal ? (
    // paladin: clicar no espaço de munição abre a escolha de flechas e bolts (de graça)
    <span key={s} class="ammo-slot" onClickCapture={(e) => { e.stopPropagation(); e.preventDefault(); props.onOpen('ammo'); }}>
      <ItemSlot item={eq(s)} target={{ place: 'equip', slot: s }} api={props.inv.api} placeholder={`${IMG}/inventory-${img}.png`} title="Munição: clique para escolher a flecha ou o bolt (grátis)" />
    </span>
  ) : (
    <ItemSlot key={s} item={eq(s)} target={{ place: 'equip', slot: s }} api={props.inv.api} placeholder={`${IMG}/inventory-${img}.png`} title={label} />
  );
  return (
    <div class="pane inventory">
      <div class="inv-main">
        <div class="inv-slots">
          {SLOTS.map(([s, img, label]) => slot(s, img, label))}
          <span class="inv-num"><small>Soul</small>{props.c.soul}</span>
          {slot('feet', 'feet', 'Botas')}
          <span class="inv-num"><small>Cap</small>{props.inv.freeCapacity ?? props.c.capacity}</span>
        </div>
        <div class="inv-states" title="Condições">
          {(props.flags ?? []).filter((f) => FLAG[f]).map((f) => (
            <i key={f} class="state-flag" title={FLAG[f]![1]} style={{ backgroundPosition: `-${FLAG[f]![0] * 9}px 0` }} />
          ))}
        </div>
      </div>
      <div class="inv-btns">
        <ImgButton title="Loja" up={`${IMG}/button-store-small-up.png`} w={52} h={16} onClick={() => props.onOpen("store")} />
        <button class="txt-btn" onClick={() => props.onOpen('stop')}>Stop</button>
        <button class="txt-btn home-btn" title="Voltar ao templo agora (de hunt, boss, quest ou treino)" onClick={() => props.onOpen('home')}>
          <svg viewBox="0 0 16 16" width="13" height="13" aria-label="Templo"><path d="M8 1.5 1 7.5h2V14h3.8V10h2.4v4H13V7.5h2Z" fill="currentColor" /></svg></button>
        <button class="txt-btn" title="Achievements (Cyclopedia)" onClick={() => props.onOpen('achievements')}>Conquistas</button>
        <button class="txt-btn" onClick={() => props.onOpen('options')}>Options</button>
        <button class="txt-btn" onClick={props.onLogout}>Logout</button>
        <button class="storebox-btn" title="Noxara Store Box: itens da loja, recompensas e eventos" onClick={() => props.onOpen('storebox')}><i />Store Box</button>
      </div>
    </div>
  );
}

export type Stance = 'offensive' | 'balanced' | 'defensive';
/** Modo de ataque, como no Tibia: ofensivo dá mais dano e defende menos; defensivo o contrário. */
function Combat(props: { stance: Stance; onStance: (s: Stance) => void }) {
  const icon: Record<Stance, string> = { offensive: '/ui/stance-offensive.png', balanced: '/ui/stance-balanced.png', defensive: '/ui/stance-defensive.png' };
  const label: Record<Stance, string> = { offensive: 'Ataque total: arma com 100% do ataque, defesa 50%, mitigação 80% (Tibia)', balanced: 'Equilibrado: arma com 75% do ataque, defesa 75%, mitigação 100% (Tibia)', defensive: 'Defesa total: arma com 50% do ataque, defesa 100%, mitigação 120% (Tibia)' };
  return (
    <div class="pane combat">
      {(['offensive', 'balanced', 'defensive'] as Stance[]).map((s) => (
        <button key={s} class={'stance' + (props.stance === s ? ' on' : '')} title={label[s]} onClick={() => props.onStance(s)}>
          <img src={icon[s]} width={16} height={16} alt={label[s]} />
        </button>
      ))}
    </div>
  );
}

export function RightPanel(props: {
  c: CharacterState; flags?: string[]; monk?: { harmony: number; serene: boolean }; skillProgress?: Record<string, number>; special?: { crit: number; critdmg: number; lifeleech: number; manaleech: number }; bank: number; stance: Stance; onStance: (s: Stance) => void; battle: BattleEntry[]; onBattleClick?: (id: string | number) => void; onBattleLook?: (id: string | number) => void; mm: MinimapPos | null; onMapOpen?: () => void; mmLabel?: string; inv: InvProps;
  onOpen: (b: ControlButton | string) => void; onSearch: () => void; onLogout: () => void;
}) {
  return (
    <aside class="side right">
      <Minimap pos={props.mm} label={props.mmLabel} onOpen={props.onMapOpen} />
      <StatusBars c={props.c} monk={props.monk} />
      <Inventory c={props.c} inv={props.inv} flags={props.flags} onOpen={(id) => props.onOpen(id)} onLogout={props.onLogout} />
      <div class="pane bankline" title="Gold do banco: o loot de gold vai direto para cá, sem peso"><span>Banco</span><b>{props.bank.toLocaleString('pt-BR')} gp</b></div>
      <Combat stance={props.stance} onStance={props.onStance} />
      <MenuPanel onPick={(it) => props.onOpen(it.open)} onSearch={props.onSearch} />
      {props.inv.open.map((id) => {
        if (id === POUCH_WINDOW) return <PouchWidget key={id} items={props.inv.items} api={props.inv.api} onClose={() => props.inv.closeContainer(id)} />;
        const box = props.inv.items.find((i) => i.id === id);
        return box ? <ContainerWidget key={id} box={box} items={props.inv.items} api={props.inv.api} onClose={() => props.inv.closeContainer(id)} /> : null;
      })}
      <BattleList entries={props.battle} onClick={props.onBattleClick} onLook={props.onBattleLook} />
      <Skills c={props.c} progress={props.skillProgress} special={props.special} />
    </aside>
  );
}
